import type { CsvParserErrorType } from './csv-parser-error'
import Papa from 'papaparse'

export class CsvParserError extends Error {
  name = 'CsvParserError'
  issues: CsvParserErrorType[]

  constructor(message: string, issues: CsvParserErrorType[]) {
    super(message)
    this.issues = issues
  }
}

export interface CsvParseMeta {
  delimiter: string
  headers: string[]
  totalRows: number
}

export type CsvParseResult<T = Record<string, string>> =
  | {
      success: true
      data: T[]
      errors: CsvParserErrorType[]
      meta: CsvParseMeta
    }
  | {
      success: false
      data: null
      errors: CsvParserErrorType[]
      meta: CsvParseMeta
    }

interface ColumnValidation {
  regex: RegExp
  message: string
}

export interface CsvParseOptions {
  delimiter?: string
  skipEmptyLines?: boolean
  dynamicTyping?: boolean
  trim?: boolean
  expectedHeaders?: string[]
  forbiddenChars?: RegExp
  columnValidations?: Record<string, ColumnValidation>
}

/**
 * A parsed cell as the text it was written as.
 *
 * The parser types its cells, so a cell of only digits comes back as a `number` — and naming inventory
 * after an ad server's numeric ids is ordinary. Anything that keys on such a cell has to read it through
 * here: a number matches nothing in a lookup keyed by name, and the name checks are regexes, which
 * stringify what they test and so reject nothing.
 */
export const asText = (value: unknown): string => (typeof value === 'string' ? value : value === undefined || value === null ? '' : String(value))

const FATAL_CODES: readonly Papa.ParseError['code'][] = ['MissingQuotes', 'TooFewFields', 'TooManyFields', 'InvalidQuotes']
const SILENT_CODES: readonly Papa.ParseError['code'][] = ['UndetectableDelimiter']

const DELIMITERS_TO_GUESS = [',', '\t', '|', ';', Papa.RECORD_SEP, Papa.UNIT_SEP]

/**
 * The delimiter papaparse 5.5 would guess for this input.
 *
 * papaparse 5.7 lets the most consistent field count win on its own, so a file mixing `,` and `;` flips
 * to `;`. The 5.5 rule also asks the winner for more fields per row. Keeping it means a file that uploaded
 * fine before the upgrade still reads the same way.
 */
const guessDelimiter = (input: string, skipEmptyLines: boolean): string => {
  let bestDelimiter: string | undefined
  let bestDelta: number | undefined
  let maxFieldCount: number | undefined

  for (const delimiter of DELIMITERS_TO_GUESS) {
    const rows = Papa.parse<string[]>(input, { delimiter, preview: 10 }).data
    let delta = 0
    let avgFieldCount = 0
    let emptyLinesCount = 0
    let fieldCountPrevRow: number | undefined

    for (const row of rows) {
      if (skipEmptyLines && row.length === 1 && row[0].length === 0) {
        emptyLinesCount++
        continue
      }
      avgFieldCount += row.length
      if (fieldCountPrevRow === undefined) {
        fieldCountPrevRow = row.length
      } else if (row.length > 0) {
        delta += Math.abs(row.length - fieldCountPrevRow)
        fieldCountPrevRow = row.length
      }
    }
    if (rows.length > 0) avgFieldCount /= rows.length - emptyLinesCount

    if ((bestDelta === undefined || delta <= bestDelta) && (maxFieldCount === undefined || avgFieldCount > maxFieldCount) && avgFieldCount > 1.99) {
      bestDelta = delta
      bestDelimiter = delimiter
      maxFieldCount = avgFieldCount
    }
  }

  return bestDelimiter ?? Papa.DefaultDelimiter
}

export class CsvParser {
  static parse<T = Record<string, string>>(csvData: string | Buffer, expectedHeaders: string[], options?: CsvParseOptions): CsvParseResult<T> {
    const fileString = String(csvData)
    if (options?.delimiter === 'auto') options.delimiter = ''
    const skipEmptyLines = options?.skipEmptyLines ?? true

    const result = Papa.parse<T>(fileString, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      trim: true,
      ...options,
      delimiter: options?.delimiter || ((input) => guessDelimiter(input, skipEmptyLines)),
    })

    const reportedErrors: Papa.ParseError[] = (result.errors ?? []).filter((e) => !SILENT_CODES.includes(e.code))

    const parsedErrors: CsvParserErrorType[] = reportedErrors.map((e) => ({ row: e.row, message: e.message }))
    const hasFatal = reportedErrors.some((e) => FATAL_CODES.includes(e.code))

    const meta: CsvParseMeta = {
      delimiter: result.meta.delimiter,
      headers: result.meta.fields || [],
      totalRows: result.data.length,
    }

    if (hasFatal) {
      return { success: false as const, data: null, errors: parsedErrors, meta }
    }

    const validationErrors: CsvParserErrorType[] = []

    if (result.data.length === 0) {
      validationErrors.push('CSV file is empty (no data rows)')
    }

    if (expectedHeaders) {
      const actual = meta.headers
      const expected = expectedHeaders

      if (actual.length !== expected.length) {
        validationErrors.push(`Wrong column count: expected ${expected.length}, found ${actual.length}`)
      }

      const missingHeaders: string[] = []
      for (const header of expected) {
        if (!actual.includes(header)) {
          missingHeaders.push(header)
        }
      }
      if (missingHeaders.length > 0) {
        validationErrors.push(`Missing headers: ${missingHeaders.join(', ')}`)
      }

      const unexpectedHeaders: string[] = []
      for (const header of actual) {
        if (!expected.includes(header)) {
          unexpectedHeaders.push(header)
        }
      }
      if (unexpectedHeaders.length > 0) {
        validationErrors.push(`Unexpected headers: ${unexpectedHeaders.join(', ')}`)
      }
    }

    if (options?.columnValidations && validationErrors.length === 0) {
      result.data.forEach((row: any, rowIndex: number) => {
        Object.entries(options.columnValidations!).forEach(([columnName, validation]) => {
          const cellValue = row[columnName]

          if (cellValue == null || cellValue === '') {
            return
          }

          const stringValue = String(cellValue)
          if (!validation.regex.test(stringValue)) {
            validationErrors.push({ row: rowIndex + 2, columnName, actualValue: stringValue, message: validation.message })
          }
        })
      })
    }

    const forbiddenRegex = options?.forbiddenChars
    if (forbiddenRegex && validationErrors.length === 0) {
      for (const [index, row] of result.data.entries()) {
        for (const value of Object.values(row as Record<string, unknown>)) {
          const strValue = String(value ?? '')
          if (forbiddenRegex.test(strValue)) {
            validationErrors.push({ row: index + 2, actualValue: strValue, message: 'contains forbidden characters' })
          }
        }
      }
    }

    if (validationErrors.length > 0) {
      return { success: false, data: null, errors: [...parsedErrors, ...validationErrors], meta }
    }

    return { success: true, data: result.data, errors: parsedErrors, meta }
  }
}
